import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuthStore } from '../../auth/authStore';
import './admin.css';

export function AdminLayout() {
  const { t, language, setLanguage } = useTranslation();
  const { logout } = useAuthStore();
  const navigate   = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const LINKS = [
    { to: '/admin/users',    label: t('admin.nav.users'),    icon: '👥' },
    { to: '/admin/monsters', label: t('admin.nav.monsters'), icon: '👾' },
    { to: '/admin/classes',  label: t('admin.nav.classes'),  icon: '🛡️' },
    { to: '/admin/spells',   label: t('admin.nav.spells'),   icon: '✨' },
    { to: '/admin/sprites',  label: t('admin.nav.sprites'),  icon: '🎨' },
    { to: '/admin/npcs',     label: t('admin.nav.npcs' as any), icon: '🧑‍🌾' },
    { to: '/admin/items',    label: t('admin.nav.items'),    icon: '🎒' },
    { to: '/admin/maps',     label: t('admin.nav.maps'),     icon: '🗺️' },
    { to: '/admin/missions', label: t('admin.nav.missions'), icon: '🌀' },
    { to: '/admin/announcers', label: t('admin.nav.announcers'), icon: '📣' },
    // Configurações do jogo: XP, dificuldades, multiplicadores
    { to: '/admin/gameconfig', label: t('admin.nav.gameconfig'), icon: '⚙️' },
    // Ranking: configuração de pontuação e exibição do leaderboard
    { to: '/admin/ranking',  label: t('admin.nav.ranking'),  icon: '🏆' },
    // Economia: carteira virtual "Ta Feito" e auditoria
    { to: '/admin/economy',  label: t('admin.nav.economy' as any),  icon: '💰' },
  ];

  const ACTION_LINKS = [
    { to: '/admin/launch', label: t('admin.nav.launch'),  icon: '⚔️' },
    // Entra no Hub de verdade (rota /user), como um jogador — não é o editor
    // de mapa (esse fica em Mapas → 🏰 Mapa Hub).
    { to: '/user',         label: t('admin.nav.hubPlay'), icon: '🏠' },
  ];

  return (
    <div className="adminRoot">
      {/* ── Mobile Header ── */}
      <header className="adminMobileHeader">
        <div className="adminMobileLogo">{t('admin.logo')}</div>
        <button className="adminMobileMenuBtn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? '✖' : '☰'}
        </button>
      </header>

      {/* ── Sidebar ── */}
      <div className={`adminSidebarOverlay ${isMenuOpen ? 'open' : ''}`} onClick={() => setIsMenuOpen(false)} />
      <aside className={`adminSidebar ${isMenuOpen ? 'open' : ''}`}>
        <div className="adminLogo">{t('admin.logo')}</div>

        <nav className="adminNav">
          <div className="adminNavSection">{t('admin.nav.management')}</div>
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setIsMenuOpen(false)}
              className={({ isActive }) => `adminNavLink${isActive ? ' active' : ''}`}
            >
              <span>{l.icon}</span>
              <span>{l.label}</span>
            </NavLink>
          ))}

          <div className="adminNavSection" style={{ marginTop: 8 }}>Combat</div>
          {ACTION_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setIsMenuOpen(false)}
              className={({ isActive }) => `adminNavLink${isActive ? ' active' : ''}`}
            >
              <span>{l.icon}</span>
              <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* ── Language Toggle ── */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)', marginBottom: 6 }}>
            {t('admin.language')}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => setLanguage('en')}
              style={{
                flex: 1,
                padding: '4px 0',
                fontSize: 12,
                borderRadius: 4,
                border: '1px solid var(--border)',
                background: language === 'en' ? 'var(--accent)' : 'transparent',
                color: language === 'en' ? '#fff' : 'var(--text-dim)',
                cursor: 'pointer',
                fontWeight: language === 'en' ? 700 : 400,
              }}
            >
              EN
            </button>
            <button
              onClick={() => setLanguage('pt')}
              style={{
                flex: 1,
                padding: '4px 0',
                fontSize: 12,
                borderRadius: 4,
                border: '1px solid var(--border)',
                background: language === 'pt' ? 'var(--accent)' : 'transparent',
                color: language === 'pt' ? '#fff' : 'var(--text-dim)',
                cursor: 'pointer',
                fontWeight: language === 'pt' ? 700 : 400,
              }}
            >
              PT
            </button>
          </div>
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <a href="/" style={{ fontSize: 11, color: 'var(--text-dim)', textDecoration: 'none' }}>{t('admin.backToGame')}</a>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '6px 0',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 4,
              border: '1px solid #c0392b',
              background: 'transparent',
              color: '#e74c3c',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            ⏻ Logout
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="adminMain">
        <Outlet />
      </main>
    </div>
  );
}
